/**
 * tasksTable.js - the Tasks page (T-06) as pure helpers: row shape, filters,
 * grouping and ordering. views/TasksView.jsx only wires these to the API.
 *
 * Paul (T-06): one table for every person's tasks - checkbox, task, owner,
 * due date, type (Lead / Opportunity / Check-in), home record, contact and
 * the home record's stage. Filters for person, type, due window and open or
 * completed; a "Group by person" switch; default order by due date with
 * overdue in red.
 *
 * Tasks are crm_actions rows (snake_case, straight from the API); accounts
 * and projects come from AppContext (camelCase). Every date here is a LOCAL
 * calendar day ('YYYY-MM-DD'), never the UTC day - toISOString() turns "due
 * today" into overdue after about 8pm Eastern.
 */
import { pickField } from '../../data/crmStages';
import { dueDay, homeOfTask, isTaskOverdue, taskHash, taskKind } from '../crm/nextStep';
import { taggedOf } from './tagged';
import { localDay } from '../../utils/localDay';

const lc = (s) =>
  String(s == null ? '' : s)
    .trim()
    .toLowerCase();

/* ── local days ──────────────────────────────────────────────────────────── */

/** A Date as its LOCAL 'YYYY-MM-DD'. */
export { localDay };

/** 'YYYY-MM-DD' plus n days, on the local calendar (DST-safe). '' when the day is not valid. */
export function addDays(day, n) {
  const m = String(day || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return localDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
}

const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

/** A timestamp (completed_at, say) as its LOCAL day; a bare day passes through; '' when blank or unreadable. */
export function dayOf(v) {
  if (v == null || v === '') return '';
  if (isDay(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? '' : localDay(d);
}

/* ── types ───────────────────────────────────────────────────────────────── */

/** Type tags, in table order. Colours match the Dashboard's My tasks card. */
export const TYPE_TAGS = {
  lead: { text: 'Lead', fg: '#7B1FA2', bg: '#F6EFFA' },
  opportunity: { text: 'Opportunity', fg: '#1565C0', bg: '#E3F2FD' },
  'check-in': { text: 'Check-in', fg: '#00695C', bg: '#E0F2F1' },
  project: { text: 'Project', fg: '#1B5E20', bg: '#E8F5E9' },
};
export const TASK_TYPES = Object.keys(TYPE_TAGS);

/**
 * The Type filter. For everyone, the default leaves out won-project tasks:
 * Paul Q10 keeps those a separate list (PM work), and the page can still show
 * them on request. For one person (Me or a name) the default is every type:
 * a task given to someone is theirs whatever it is on (10/8/26).
 */
export const TYPE_FILTERS = [
  { value: 'default', label: 'Lead, opportunity and check-in (one person: every type)', types: ['lead', 'opportunity', 'check-in'] },
  { value: 'lead', label: 'Lead', types: ['lead'] },
  { value: 'opportunity', label: 'Opportunity', types: ['opportunity'] },
  { value: 'check-in', label: 'Check-in', types: ['check-in'] },
  { value: 'project', label: 'Project', types: ['project'] },
  { value: 'all', label: 'Every type', types: TASK_TYPES },
];

/** 'lead' | 'opportunity' | 'check-in' | 'project' | 'other' (nextStep.taskKind). */
export const taskTypeOf = (t, projectsById) => taskKind(t, projectsById);

/* ── due windows ─────────────────────────────────────────────────────────── */

export const DUE_FILTERS = [
  { value: 'all', label: 'Any due date' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: '7d', label: 'Next 7 days' },
  { value: '30d', label: 'Next 30 days' },
  { value: 'custom', label: 'Custom dates' },
];

/**
 * The window as { from, to } local days (either may be '' for open-ended),
 * or null for "any date". "Next 7 days" is today and the six days after it;
 * Overdue is everything before today (open tasks only - see matchesDue).
 */
export function dueRange(window, today, custom = {}) {
  switch (window) {
    case 'overdue':
      return { from: '', to: addDays(today, -1) };
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: today, to: addDays(today, 6) };
    case '30d':
      return { from: today, to: addDays(today, 29) };
    case 'custom': {
      let from = isDay(custom.from) ? custom.from : '';
      let to = isDay(custom.to) ? custom.to : '';
      if (from && to && from > to) [from, to] = [to, from]; // typed backwards: swap, don't show nothing
      return from || to ? { from, to } : null;
    }
    default:
      return null;
  }
}

/** Does the task's due day fall in the window? A completed task is never overdue. */
export function matchesDue(t, window, today, custom) {
  if (window === 'overdue') return !!t && t.status === 'open' && isTaskOverdue(t, today);
  const r = dueRange(window, today, custom);
  if (!r) return true;
  const d = dueDay(t);
  if (!d) return false;
  return (!r.from || d >= r.from) && (!r.to || d <= r.to);
}

/* ── owners ──────────────────────────────────────────────────────────────── */

/**
 * The owner as a person's display name. Owners are display names from the
 * user picker, but older rows can hold an email - those become the user's
 * name so they group and filter with that person.
 */
export function ownerName(owner, users) {
  const o = String(owner == null ? '' : owner).trim();
  if (!o.includes('@')) return o;
  const hit = (users || []).find((u) => lc(u && u.email) === lc(o));
  return (hit && hit.name) || o;
}
export const ownerKey = (owner, users) => lc(ownerName(owner, users));

/**
 * "Me", the way the server's owner=me reads it: my display name or my email,
 * any case - as the owner OR as one of the people tagged on it (10/6/26).
 */
export function isMine(t, user, users) {
  if (!t || !user) return false;
  const mine = [user.name, user.email].map(lc).filter(Boolean);
  const me = (name) => mine.includes(lc(name)) || mine.includes(ownerKey(name, users));
  return me(t.owner) || taggedOf(t).some(me);
}

/** Every person the Owner filter offers: registered users plus any other owner on the tasks, by name. */
export function ownerOptions(tasks, users) {
  const byKey = new Map();
  for (const u of users || []) if (u && u.name) byKey.set(lc(u.name), u.name);
  for (const t of tasks || []) {
    const name = ownerName(t && t.owner, users);
    if (name && !byKey.has(lc(name))) byKey.set(lc(name), name);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

/* ── filters (remembered per user) ───────────────────────────────────────── */

export const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'complete', label: 'Completed' },
];

export const DEFAULT_FILTERS = {
  owner: 'all',
  type: 'default',
  due: 'all',
  from: '',
  to: '',
  status: 'open',
  group: false,
};

/**
 * Stored filters, checked field by field: a stale or hand-edited value falls
 * back to the default instead of hiding every task. `owner` is 'all', 'me' or
 * a person's name.
 */
export function cleanFilters(raw) {
  const f = raw && typeof raw === 'object' ? raw : {};
  const oneOf = (list, v, dflt) => (list.some((x) => x.value === v) ? v : dflt);
  const owner = typeof f.owner === 'string' && f.owner.trim() ? f.owner.trim() : DEFAULT_FILTERS.owner;
  return {
    owner,
    type: oneOf(TYPE_FILTERS, f.type, DEFAULT_FILTERS.type),
    due: oneOf(DUE_FILTERS, f.due, DEFAULT_FILTERS.due),
    from: isDay(f.from) ? f.from : '',
    to: isDay(f.to) ? f.to : '',
    status: oneOf(STATUS_FILTERS, f.status, DEFAULT_FILTERS.status),
    group: f.group === true,
  };
}

/* ── rows ────────────────────────────────────────────────────────────────── */

/**
 * The home record's stage: a lead's pipeline stage (the company's when the
 * site inherits it), or an opportunity's sales stage (its status when it has
 * none, and for a won project).
 */
export function homeStageOf(home) {
  if (!home || !home.record) return '';
  const r = home.record;
  if (home.kind === 'lead') return pickField(r, 'stageEffective', 'stage_effective') || r.stage || '';
  if (home.kind === 'project') return r.status || '';
  return pickField(r, 'salesStage', 'sales_stage') || r.status || '';
}

/**
 * One table row per task:
 *   { id, task, description, owner, ownerKey, tagged, taggedKeys, due, overdue,
 *     type, typeTag, homeLabel, homeHash, contact, stage, completedAt, completedDay }
 * `tagged` are the names tagged on it (by name, like owner); taggedKeys theirs
 * for the Person filter.
 * A home that is not loaded keeps the task's stored label and no link.
 * completedAt is the raw timestamp (for ordering); completedDay is its local
 * day (for showing), never the UTC day of the ISO string.
 */
export function buildTaskRows(tasks, { accountsById, projectsById, contactsById, users, today }) {
  return (tasks || []).filter(Boolean).map((t) => {
    const home = homeOfTask(t, accountsById, projectsById);
    const type = taskTypeOf(t, projectsById);
    return {
      id: String(t.id),
      task: t,
      description: t.description || '',
      owner: ownerName(t.owner, users),
      ownerKey: ownerKey(t.owner, users),
      tagged: taggedOf(t).map((n) => ownerName(n, users)),
      taggedKeys: taggedOf(t).map((n) => ownerKey(n, users)),
      due: dueDay(t),
      overdue: t.status === 'open' && isTaskOverdue(t, today),
      type,
      typeTag: TYPE_TAGS[type] || null,
      homeLabel: (home && home.label) || t.entity_label || '',
      homeHash: home ? taskHash(t, projectsById) : null,
      contact: t.contact_id && contactsById ? contactsById.get(String(t.contact_id)) || null : null,
      stage: homeStageOf(home),
      completedAt: t.completed_at || null,
      completedDay: dayOf(t.completed_at),
    };
  });
}

/**
 * Rows that pass every filter. `ctx` = { today, user, users }. A person (Me or
 * a name) matches a task they own or are tagged on.
 */
export function filterRows(rows, filters, { today, user, users }) {
  const f = cleanFilters(filters);
  const want = lc(f.owner);
  const types =
    f.type === 'default' && want !== 'all' ? TASK_TYPES : (TYPE_FILTERS.find((x) => x.value === f.type) || TYPE_FILTERS[0]).types;
  return (rows || []).filter((r) => {
    if (r.task.status !== f.status) return false;
    if (!types.includes(r.type)) return false;
    if (want === 'me') {
      if (!isMine(r.task, user, users)) return false;
    } else if (want !== 'all' && r.ownerKey !== want && !(r.taggedKeys || []).includes(want)) return false;
    return matchesDue(r.task, f.due, today, { from: f.from, to: f.to });
  });
}

/**
 * Order: open tasks by due date, overdue first and undated last (Paul's
 * default); completed tasks most recently completed first.
 */
export function sortRows(rows, status, today) {
  if (status === 'complete') {
    return [...(rows || [])].sort(
      (a, b) => String(b.completedAt || '').localeCompare(String(a.completedAt || '')) || b.due.localeCompare(a.due)
    );
  }
  // the same order as nextStep.sortOverdueFirst (the Dashboard cards), on rows
  const rank = (r) => (isTaskOverdue(r.task, today) ? 0 : r.due ? 1 : 2);
  return [...(rows || [])].sort((a, b) => rank(a) - rank(b) || a.due.localeCompare(b.due) || a.description.localeCompare(b.description));
}

/** "Group by person": [{ key, owner, rows, overdue }] by name, rows kept in their order. */
export function groupByOwner(rows) {
  const groups = new Map();
  for (const r of rows || []) {
    const key = r.ownerKey || '';
    if (!groups.has(key)) groups.set(key, { key, owner: r.owner || 'No owner', rows: [], overdue: 0 });
    const g = groups.get(key);
    g.rows.push(r);
    if (r.overdue) g.overdue += 1;
  }
  return [...groups.values()].sort((a, b) => a.owner.localeCompare(b.owner));
}
